// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useState } from 'react';

/**
 * The tuned threshold profiles `training/tune_thresholds.py --register` has written, and the
 * one action taken on them: promoting one into force for its imagery provider, or demoting it.
 *
 * Registering is not shipping. A profile sits inert until it is promoted here — analysis runs
 * use the thresholds the analyser ships, exactly as they did before this existed — and demoting
 * puts them back within one run, with no deploy. That is also the rollback.
 */

/**
 * One field group of a profile: the fields that move together when it is promoted, and where
 * that group stands. The unit of promotion is the group because that is how the tuning is
 * actually judged — after the 2026-08-20 search the bunker and water values were worth shipping
 * while its fairway and green values were an overfit.
 */
export type MlThresholdProfileGroup = {
  /** 'water' | 'bunker' | 'grass' | 'fairway' | 'green', or 'all' for a pre-groups profile. */
  name: string;
  /** The fields this group would actually change against the baseline. */
  changedFields: string[];
  /** The classes it is judged on — 'grass' answers for fairway and green both. */
  kinds: string[];
  /** This group regressed on holdout: the server refuses to promote it. */
  regressesOnHoldout: boolean;
  promotedAt: string | null;
  promotedBy: string | null;
};

export type MlThresholdProfile = {
  id: string;
  provider: string;
  version: string;
  /** Every field of the analyser's Thresholds dataclass, as the tuner wrote it. */
  profile: Record<string, number>;
  /** What the search started from, so the row can say what the tuning changed. */
  baselineProfile: Record<string, number>;
  /** 'shipped' (the hard-coded profile) or 'promoted' (whatever was in force). */
  baselineSource: string;
  tunedAt: string | null;
  seed: number;
  trainCrops: number;
  holdoutCrops: number;
  trainObjective: number;
  trainBaselineObjective: number;
  /** Null when the run had no holdout courses, which is an ordinary state and not a zero. */
  holdoutObjective: number | null;
  holdoutBaselineObjective: number | null;
  /** Scored worse on holdout than its baseline: the server refuses the whole profile. */
  regressesOnHoldout: boolean;
  /** What this profile can be promoted in, one class at a time. */
  groups: MlThresholdProfileGroup[];
};

type Row = Record<string, unknown>;

const num = (row: Row, ...keys: string[]): number => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return 0;
};

const numOrNull = (row: Row, ...keys: string[]): number | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
};

const str = (row: Row, ...keys: string[]): string | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
};

const bool = (row: Row, ...keys: string[]): boolean => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'boolean') return value;
  }
  return false;
};

/** The profile columns travel as jsonb strings; a malformed one costs its card, not the page. */
const profileOf = (row: Row, ...keys: string[]): Record<string, number> => {
  const raw = str(row, ...keys);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, number] => typeof entry[1] === 'number'
      )
    );
  } catch {
    return {};
  }
};

const strings = (row: Row, ...keys: string[]): string[] => {
  for (const key of keys) {
    const value = row[key];
    if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  }
  return [];
};

const groupsOf = (row: Row): MlThresholdProfileGroup[] => {
  const raw = row.groups ?? row.Groups;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((entry): entry is Row => !!entry && typeof entry === 'object')
    .map((entry) => ({
      name: str(entry, 'name', 'Name') ?? '',
      changedFields: strings(entry, 'changedFields', 'ChangedFields'),
      kinds: strings(entry, 'kinds', 'Kinds'),
      regressesOnHoldout: bool(entry, 'regressesOnHoldout', 'RegressesOnHoldout'),
      promotedAt: str(entry, 'promotedAt', 'PromotedAt'),
      promotedBy: str(entry, 'promotedBy', 'PromotedBy'),
    }))
    .filter((group) => group.name.length > 0);
};

export const mapMlThresholdProfile = (row: Row): MlThresholdProfile | null => {
  const id = str(row, 'id', 'Id');
  const provider = str(row, 'provider', 'Provider');
  const version = str(row, 'version', 'Version');
  if (!id || !provider || !version) return null;
  return {
    id,
    provider,
    version,
    profile: profileOf(row, 'profileJson', 'ProfileJson'),
    baselineProfile: profileOf(row, 'baselineProfileJson', 'BaselineProfileJson'),
    baselineSource: str(row, 'baselineSource', 'BaselineSource') ?? 'shipped',
    tunedAt: str(row, 'tunedAt', 'TunedAt'),
    seed: num(row, 'seed', 'Seed'),
    trainCrops: num(row, 'trainCrops', 'TrainCrops'),
    holdoutCrops: num(row, 'holdoutCrops', 'HoldoutCrops'),
    trainObjective: num(row, 'trainObjective', 'TrainObjective'),
    trainBaselineObjective: num(row, 'trainBaselineObjective', 'TrainBaselineObjective'),
    holdoutObjective: numOrNull(row, 'holdoutObjective', 'HoldoutObjective'),
    holdoutBaselineObjective: numOrNull(row, 'holdoutBaselineObjective', 'HoldoutBaselineObjective'),
    regressesOnHoldout: bool(row, 'regressesOnHoldout', 'RegressesOnHoldout'),
    groups: groupsOf(row),
  };
};

/** Is any of this profile's field groups currently in force? */
export const isPromoted = (profile: MlThresholdProfile): boolean =>
  profile.groups.some((group) => group.promotedAt !== null);

/** The fields whose value differs from the baseline — what the search actually moved. */
export const changedFields = (profile: MlThresholdProfile): string[] =>
  Object.keys(profile.profile)
    .filter((field) => profile.profile[field] !== profile.baselineProfile[field])
    .sort();

export type MlThresholdProfilesState = {
  profiles: MlThresholdProfile[];
  loading: boolean;
  error: 'forbidden' | 'unavailable' | 'failed' | null;
  /** The server's own reason for refusing the last promote, so the page can show it verbatim. */
  actionError: string | null;
  busyProfileId: string | null;
  /** Names the field groups to move; an empty list means every group of the profile. */
  promote: (profileId: string, groups?: string[]) => Promise<void>;
  demote: (profileId: string, groups?: string[]) => Promise<void>;
};

export function useMlThresholdProfiles(
  apiBaseUrl: string,
  token: string | null | undefined,
  enabled: boolean
): MlThresholdProfilesState {
  const [profiles, setProfiles] = useState<MlThresholdProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<MlThresholdProfilesState['error']>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyProfileId, setBusyProfileId] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!enabled || !token) {
      setProfiles([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/geodata/ml/threshold-profiles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!response.ok) {
          setError(response.status === 403 ? 'forbidden' : response.status === 404 ? 'unavailable' : 'failed');
          return;
        }
        const body = (await response.json()) as Row;
        const rows = body.profiles ?? body.Profiles;
        setProfiles(
          (Array.isArray(rows) ? (rows as Row[]) : [])
            .map(mapMlThresholdProfile)
            .filter((p): p is MlThresholdProfile => p !== null)
        );
      } catch {
        if (!cancelled) setError('failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, enabled, token, reloadToken]);

  const act = useCallback(
    async (profileId: string, action: 'promote' | 'demote', groups?: string[]) => {
      if (!token) return;
      setBusyProfileId(profileId);
      setActionError(null);
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/geodata/ml/threshold-profiles/${profileId}/${action}`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ groups: groups ?? [] }),
          }
        );
        if (!response.ok) {
          // geodata refuses an overfit profile with a 400 and says why; the BFF passes that
          // body through so the reason can be shown instead of a generic failure.
          const body = (await response.json().catch(() => null)) as Row | null;
          setActionError(
            (body && (str(body, 'detail', 'Detail') ?? str(body, 'title', 'Title'))) ?? 'failed'
          );
          return;
        }
        setReloadToken((token_) => token_ + 1);
      } catch {
        setActionError('failed');
      } finally {
        setBusyProfileId(null);
      }
    },
    [apiBaseUrl, token]
  );

  const promote = useCallback(
    (profileId: string, groups?: string[]) => act(profileId, 'promote', groups),
    [act]
  );
  const demote = useCallback(
    (profileId: string, groups?: string[]) => act(profileId, 'demote', groups),
    [act]
  );

  return { profiles, loading, error, actionError, busyProfileId, promote, demote };
}
